import {clsx} from 'clsx';
import type {UseMultipleSelectionGetDropdownReturnValue} from 'downshift';
import {memo} from 'react';
import type {CSSProperties, PropsWithChildren} from 'react';

import * as styles from './style.module.scss';

type AnchorProps = {
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  onKeyDown: () => void;
  onClick: () => void;
  variant: (typeof AnchorVariants)[keyof typeof AnchorVariants];
  isFullWidth?: boolean;
  id?: string;
} & UseMultipleSelectionGetDropdownReturnValue;

const AnchorVariants = {
  bordered: 'bordered',
  borderless: 'borderless',
  transparent: 'transparent',
  transparentBordered: 'transparent-bordered',
  pill: 'pill',
} as const;
type AnchorVariants = (typeof AnchorVariants)[keyof typeof AnchorVariants];
const Anchor = memo(function Anchor({
  id,
  className,
  style,
  children,
  disabled,
  variant,
  onKeyDown,
  onClick,
  isFullWidth = false,
  ...props
}: PropsWithChildren<AnchorProps>) {
  const appliedStyle = isFullWidth
    ? {
        ...style,
        width: '100%',
        maxWidth: 'none',
        justifyContent: 'space-between',
      }
    : style;
  const isPill = variant === AnchorVariants.pill;
  const classes = clsx(
    styles.anchor,
    {
      [styles.bordered]: variant.includes('bordered') || isPill,
      [styles.transparent]: variant.includes('transparent') || isPill,
      [styles.pill]: isPill,
      [styles.fullWidth]: isFullWidth,
      [styles.disabled]: disabled,
    },
    className
  );

  return (
    <div
      {...props}
      id={id}
      role="listbox"
      tabIndex={0}
      className={classes}
      onClick={evt => {
        if (disabled) return;
        // Let the toggle menu button default behavior handle the click
        if (
          evt.target instanceof HTMLElement &&
          !['button', 'label'].includes(evt.target.tagName.toLowerCase())
        ) {
          onClick();
        }
      }}
      onKeyDown={evt => {
        if (disabled) return;
        if (evt.key === 'Enter') {
          onKeyDown();
        }
      }}
      style={appliedStyle}
    >
      {children}
    </div>
  );
});

export type {AnchorProps};
export type AnchorVariant =
  (typeof AnchorVariants)[keyof typeof AnchorVariants];
export {Anchor, AnchorVariants};
